import { Box, Stack, Typography } from "@mui/material";
import { AppAvatar } from "components";
import { KeyConstant } from "const";
import { memo, useEffect, useState } from "react";
import { StorageUtil } from "utils";
import { getCreatedTimeText, useMessageStyle } from "./ChatItem.helper";
import { getInteractor } from "services/local.service";
import { getCommonLang } from "utils/lang.utils";
import clsx from "clsx";
import ChatTypography from "./ChatTypography";
import { SCROLL_TRIGGER_CLASS } from "../useQueryMessage";
import { makeStyles } from "@mui/styles";
import Quote from "../MessengerChatInput/Quote";
import { refactorMessage } from "../ViewMode.helper";
import { PinFilledIcon, TimerIcon } from "components/icons";
import { useAlertContext } from "components/context/AlertContext";
import { useDispatch } from "react-redux";
import { ConversationActions } from "redux-store";
import EditMessage from "./EditMessage";
import { getLatestMessage } from "utils/view.utils";
import { useCleanUpEffect } from "hooks";
import ThreadParentLine from "./ThreadParentLine";

const QuoteMessage = ({ message, isEditing, onCloseEdit }) => {
  const messengerClasses = useMessageStyle();
  const msgCreatedTime = getCreatedTimeText(message);
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const isMine = message.senderId === accountId;
  const classes = useStyles();
  const { showAlert } = useAlertContext();
  const dispatch = useDispatch();
  const { isMounted } = useCleanUpEffect();

  const [quoteMessage, setQuoteMessage] = useState({});

  const handleSetQuoteMessage = async sourceId => {
    const quoteMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
      source_id: sourceId,
    });
    if (quoteMessage && quoteMessage.id) {
      const refactorQuoteMessage = await refactorMessage(quoteMessage);
      const latestQuoteMessage = await getLatestMessage(prefixKey, refactorQuoteMessage);
      if (isMounted()) setQuoteMessage(latestQuoteMessage);
    } else {
      if (isMounted())
        setQuoteMessage({
          senderName: getCommonLang("TXT_USER_TRIOS"),
          content: getCommonLang("TXT_NOT_FOUND_MESSAGE"),
        });
    }
  };

  const handleJump2Quote = async () => {
    if (isEditing) return;

    if (quoteMessage && quoteMessage.sourceId) {
      dispatch(
        ConversationActions.setSelectGroupId({
          selectedGroupId: quoteMessage.groupId,
          threadingId: quoteMessage.threadId,
          scrollToChildId: quoteMessage.sourceId,
        }),
      );
    } else {
      showAlert({ content: getCommonLang("TXT_NOT_FOUND_MESSAGE"), alertProps: { severity: "error" } });
    }
  };

  useEffect(() => {
    if (message && message.quoteId) {
      handleSetQuoteMessage(message.quoteId);
    } else {
      setQuoteMessage({});
    }
  }, [message]);

  const isShowMsgCreatedTime = !isMine && msgCreatedTime;

  return (
    <Stack direction="row" spacing={2} onClick={handleJump2Quote}>
      {!isMine && (
        <AppAvatar
          avatarId={message.avatarId}
          size={40}
          className={clsx(!Boolean(message.isAvatar) && messengerClasses.hiddenAvatar)}
          alt="chatting-avatar"
        />
      )}

      <Box>
        {isShowMsgCreatedTime && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {message.isDisappear && <TimerIcon />}
            <Typography
              className={clsx("ellipsis", messengerClasses.createdTimeText, "preview-time-text", "msg-sender-time")}
            >
              {msgCreatedTime}
            </Typography>
          </Stack>
        )}

        {Boolean(message.sendAsDirectMessage) && <ThreadParentLine message={message} />}

        <Stack direction={isMine ? "row-reverse" : "row"}>
          <Stack
            direction="column"
            className={clsx(messengerClasses.messengerRoot, isMine && messengerClasses.myMessage, SCROLL_TRIGGER_CLASS)}
          >
            {isEditing ? (
              <EditMessage message={message} onCloseEdit={onCloseEdit} />
            ) : (
              <>
                <Quote
                  quoteMessage={quoteMessage}
                  className={clsx(classes.quoteMsg, SCROLL_TRIGGER_CLASS, isMine && classes.quote)}
                />

                <ChatTypography
                  messageContent={message.content}
                  mentions={message.mentions}
                  className={clsx(messengerClasses.spacing, "preview-chat")}
                  messageId={message.id}
                  messageSourceId={message.sourceId}
                />
              </>
            )}
          </Stack>

          {(message.isPin || message.isDisappear) && (
            <Stack justifyContent="space-between" mr={0.5}>
              <Box>{message.isPin && <PinFilledIcon />}</Box>
              {isMine && message.isDisappear && <TimerIcon />}
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default memo(QuoteMessage);

const useStyles = makeStyles(theme => ({
  quoteMsg: {
    zIndex: 1,
  },

  quote: {
    backgroundColor: theme.palette.primary.main,
  },
}));
